
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AnmalanTradslag, TradslagDTO, VKAtgardMbf } from "~app/features/virkeskop/models/mbf-models";
import { AtgardMbfService } from "~app/features/virkeskop/services/atgard-mbf.service";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { ForyngringTradslagComponent } from "./foryngring-tradslag/foryngring-tradslag.component";

@Component({
    selector: "rask-foryngringsatgard",
    templateUrl: "./foryngringsatgard.component.html",
    styleUrls: ["./foryngringsatgard.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        ForyngringTradslagComponent,
        ActionButtonComponent
    ]
})

export class ForyngringsatgardComponent implements OnInit, OnDestroy {

    anmalanForyngringsmetoder: string[] = [];
    anmalanForyngringsmetod: string;
    allatradslag: TradslagDTO[];

    @Input() atgardMbf: VKAtgardMbf;
    @Input() readonly = false;

    @Output() foryngringsatgardChanged = new EventEmitter();

    private readonly atgardMbfService = inject(AtgardMbfService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private atgard: Atgard;

    private readonly destroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(atgard => this.atgard = atgard);

        this.atgardMbfService.getTradslag().subscribe({
            next: tradslag => {
                this.allatradslag = tradslag;
            },
            error: () => {
                this.allatradslag = [];
            }
        });

        this.atgardMbfService.getForyngringsmetoder().subscribe({
            next: metoder => {
                this.anmalanForyngringsmetoder = metoder;
                this.anmalanForyngringsmetod = this.atgardMbf.anmalanForyngringsmetod;
            },
            error: () => {
                this.anmalanForyngringsmetoder = [];
            }
        });
    }

    metodChanged() {
        if (ObjectUtils.hasValue(this.anmalanForyngringsmetod)) {
            this.atgardMbfService.updateForyngringsMetod(this.atgard.guid, this.anmalanForyngringsmetod);

            if(this.atgardMbf.anmalanTradslag.length === 0) {
                this.addRow();
            }
        }
    }

    addRow() {
        if (this.readonly) {
            return;
        }
        const model = {
            tradslagId: null,
            antalPlantor: 0,
            arealHa: this.getArealRest(),
            guid: GuidUtils.newGuid()
        } as AnmalanTradslag;

        this.atgardMbf.anmalanTradslag.push(model);
        this.atgardMbfService.addAnmalanTradslag(model.guid);
        this.atgardMbfService.updateAnmalanTradslag(
            model.guid,
            model.id,
            model.arealHa,
            model.antalPlantor
        );
    }

    deleted(tradslag: AnmalanTradslag) {
        const delCandidate = this.atgardMbf.anmalanTradslag.indexOf(tradslag);
        this.atgardMbf.anmalanTradslag.splice(delCandidate, 1);
        this.atgardMbfService.deleteAnmalanTradslag(tradslag.guid);
    }

    private getArealRest(): number {
        const total = this.atgardMbfService.getTotalArea(this.atgardMbf.anmalanTradslag);
        const rest = (this.atgard.nettoareal - total);
        return rest > 0 ? _.round(rest, 1) : 0;
    }

}
